import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Save, ArrowLeft, Tags, Wallet } from "lucide-react";
import { supabase } from "../../../Services/supabaseClient";
import { useCompany } from "../../../context/CompanyContext";
import "./fixedAssets.css";
import { formatNaira, round2 } from "./faMoney";
import { fetchCompanyCOA } from "./coaFetch";

const DEFAULT_METHOD = "SL";

function monthsBetweenInclusive(startDate, endDate) {
  const s = new Date(`${startDate}T00:00:00`);
  const e = new Date(`${endDate}T00:00:00`);
  const months =
    (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth());
  return Math.max(0, months + 1);
}

export default function FixedAssetForm() {
  const { companyId } = useCompany();
  const nav = useNavigate();
  const params = useParams();

  const assetId = params.assetId;
  const isEdit = Boolean(assetId);

  const [coa, setCoa] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Acquisition posting UI (Phase 3.6)
  const [postAcqNow, setPostAcqNow] = useState(false);
  const [creditAccountId, setCreditAccountId] = useState("");

  const [form, setForm] = useState({
    asset_tag: "", // optional now (auto-tag)
    name: "",
    description: "",
    category_id: "",
    category: "",

    purchase_date: "",
    in_service_date: "",
    cost: "",
    residual_value: "0",
    useful_life_months: "36",
    depreciation_method: DEFAULT_METHOD,
    status: "ACTIVE",
    prorate_first_month: false,

    location: "",
    department: "",
    custodian: "",
    vendor_name: "",
    invoice_ref: "",

    asset_account_id: "",
    accum_dep_account_id: "",
    dep_expense_account_id: "",
  });

  function setField(key, val) {
    setForm((p) => ({ ...p, [key]: val }));
  }

  const computed = useMemo(() => {
    const cost = Number(form.cost || 0);
    const residual = Number(form.residual_value || 0);
    const life = Number(form.useful_life_months || 0);
    const base = Math.max(0, cost - residual);
    const monthly = life > 0 ? round2(base / life) : 0;

    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const currentMonthFirst = `${yyyy}-${mm}-01`;

    const start = form.in_service_date || currentMonthFirst;
    const months = monthsBetweenInclusive(start, currentMonthFirst);
    const year1 = round2(Math.min(base, monthly * 12));

    return { base, monthly, months, year1 };
  }, [form]);

  async function fetchCOA() {
    const data = await fetchCompanyCOA(companyId);
    setCoa(data);
  }

  async function fetchCats() {
    const { data, error } = await supabase
      .from("fixed_asset_categories")
      .select("*")
      .eq("company_id", companyId)
      .order("name", { ascending: true });

    if (!error) setCats(data || []);
  }

  async function loadAssetIfEdit() {
    if (!isEdit) return;

    const { data, error } = await supabase
      .from("fixed_assets")
      .select("*")
      .eq("company_id", companyId)
      .eq("id", assetId)
      .single();

    if (error) {
      alert(error.message);
      nav("/fixed-assets");
      return;
    }

    setForm({
      asset_tag: data.asset_tag || "",
      name: data.name || "",
      description: data.description || "",
      category_id: data.category_id || "",
      category: data.category || "",

      purchase_date: data.purchase_date || "",
      in_service_date: data.in_service_date || "",
      cost: String(data.cost ?? ""),
      residual_value: String(data.residual_value ?? 0),
      useful_life_months: String(data.useful_life_months ?? 36),
      depreciation_method: data.depreciation_method || DEFAULT_METHOD,
      status: data.status || "ACTIVE",
      prorate_first_month: Boolean(data.prorate_first_month),

      location: data.location || "",
      department: data.department || "",
      custodian: data.custodian || "",
      vendor_name: data.vendor_name || "",
      invoice_ref: data.invoice_ref || "",

      asset_account_id: data.asset_account_id || "",
      accum_dep_account_id: data.accum_dep_account_id || "",
      dep_expense_account_id: data.dep_expense_account_id || "",
    });

    // Acquisition posting: never auto-post on edit (safe)
    setPostAcqNow(false);
    setCreditAccountId("");
  }

  useEffect(() => {
    if (!companyId) return;
    (async () => {
      setLoading(true);
      await Promise.all([fetchCOA(), fetchCats()]);
      await loadAssetIfEdit();
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, assetId]);

  const assetAccounts = useMemo(
    () => coa.filter((a) => a.type === "Asset"),
    [coa]
  );
  const expenseAccounts = useMemo(
    () => coa.filter((a) => a.type === "Expense"),
    [coa]
  );

  // Credit accounts for acquisition: typically Bank/Cash (Asset) or Payables (Liability)
  const acquisitionCreditAccounts = useMemo(() => {
    const allowed = coa.filter(
      (a) => a.type === "Asset" || a.type === "Liability"
    );
    // Move likely choices to top: bank/cash/payable
    const score = (a) => {
      const name = String(a.name || "").toLowerCase();
      const cat = String(a.category || "").toLowerCase();
      let s = 0;
      if (cat.includes("bank") || name.includes("bank")) s += 50;
      if (cat.includes("cash") || name.includes("cash")) s += 45;
      if (
        cat.includes("payable") ||
        name.includes("payable") ||
        name.includes("creditor")
      )
        s += 40;
      return s;
    };
    return [...allowed].sort(
      (x, y) =>
        score(y) - score(x) || String(x.name).localeCompare(String(y.name))
    );
  }, [coa]);

  // Auto-pick a sensible default credit account once COA loads (only for add flow)
  useEffect(() => {
    if (isEdit) return;
    if (creditAccountId) return;
    const pick = acquisitionCreditAccounts[0];
    if (pick?.id) setCreditAccountId(pick.id);
  }, [acquisitionCreditAccounts, creditAccountId, isEdit]);

  function applyCategoryDefaults(categoryId) {
    const cat = cats.find((c) => c.id === categoryId);
    if (!cat) return;

    // set friendly category label too (for lists / exports)
    setForm((p) => ({
      ...p,
      category_id: cat.id,
      category: cat.name,

      useful_life_months: cat.default_useful_life_months
        ? String(cat.default_useful_life_months)
        : p.useful_life_months,
      residual_value:
        cat.default_residual_value != null
          ? String(cat.default_residual_value)
          : p.residual_value,

      asset_account_id: cat.default_asset_account_id || p.asset_account_id,
      accum_dep_account_id:
        cat.default_accum_dep_account_id || p.accum_dep_account_id,
      dep_expense_account_id:
        cat.default_dep_expense_account_id || p.dep_expense_account_id,
    }));
  }

  async function callEdgeFunction(fnName, payload) {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;
    if (!token) throw new Error("Not authenticated.");

    const fnUrl = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/${fnName}`;

    const res = await fetch(fnUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    const json = await res.json();
    if (!res.ok) {
      const msg = json?.error || "Request failed.";
      const err = new Error(msg);
      err.status = res.status;
      throw err;
    }
    return json;
  }

  async function save() {
    if (!companyId) return;

    const required = [
      "name",
      "purchase_date",
      "in_service_date",
      "cost",
      "useful_life_months",
      "asset_account_id",
      "accum_dep_account_id",
      "dep_expense_account_id",
    ];

    for (const k of required) {
      if (!String(form[k] || "").trim()) {
        alert(`Please fill: ${k.replaceAll("_", " ")}`);
        return;
      }
    }

    if (!isEdit && postAcqNow && !creditAccountId) {
      alert(
        "Please select a Paid from / Payable account (credit) to post acquisition."
      );
      return;
    }

    const payload = {
      company_id: companyId,

      // asset_tag is OPTIONAL: if empty, trigger will generate.
      asset_tag: form.asset_tag?.trim() ? form.asset_tag.trim() : null,

      name: form.name.trim(),
      description: form.description.trim(),

      category_id: form.category_id || null,
      category: form.category?.trim() || null,

      purchase_date: form.purchase_date,
      in_service_date: form.in_service_date,
      cost: Number(form.cost || 0),
      residual_value: Number(form.residual_value || 0),
      useful_life_months: Number(form.useful_life_months || 0),

      depreciation_method: form.depreciation_method,
      depreciation_frequency: "monthly",
      status: form.status,

      prorate_first_month: Boolean(form.prorate_first_month),

      location: form.location.trim(),
      department: form.department.trim(),
      custodian: form.custodian.trim(),
      vendor_name: form.vendor_name.trim(),
      invoice_ref: form.invoice_ref.trim(),

      asset_account_id: form.asset_account_id,
      accum_dep_account_id: form.accum_dep_account_id,
      dep_expense_account_id: form.dep_expense_account_id,
    };

    setSaving(true);

    try {
      if (isEdit) {
        const { error } = await supabase
          .from("fixed_assets")
          .update(payload)
          .eq("company_id", companyId)
          .eq("id", assetId);

        if (error) return alert(error.message);
        nav(`/fixed-assets/fixed-assets/${assetId}`);
        return;
      }

      const { data, error } = await supabase
        .from("fixed_assets")
        .insert(payload)
        .select("id")
        .single();

      if (error) return alert(error.message);

      // Phase 3.6: Post acquisition journal now (optional)
      if (postAcqNow) {
        try {
          await callEdgeFunction("fixed-assets-post-acquisition", {
            company_id: companyId,
            asset_id: data.id,
            credit_account_id: creditAccountId,
          });
        } catch (e) {
          // Don’t block asset creation; just notify.
          const status = e?.status;
          if (status === 409) {
            alert(
              "Asset saved. Acquisition journal was already posted for this asset."
            );
          } else {
            alert(
              `Asset saved, but acquisition journal could not be posted:\n${
                e.message || "Unknown error"
              }`
            );
          }
        }
      }

      nav(`/fixed-assets/fixed-assets/${data.id}`);
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="fa-wrap">
        <div style={{ opacity: 0.8 }}>Loading…</div>
      </div>
    );
  }

  return (
    <div className="fa-wrap">
      <div className="fa-header">
        <div className="fa-title">
          <h2>{isEdit ? "Edit Asset" : "Add Asset"}</h2>
          <p>
            Choose a category to auto-fill defaults and keep your register
            consistent.
          </p>
        </div>
        <div className="fa-actions">
          <button className="fa-btn" onClick={() => nav(-1)} disabled={saving}>
            <ArrowLeft size={16} /> Back
          </button>
          <button
            className="fa-btn"
            onClick={() => nav("/fixed-assets/fixed-asset-categories")}
            disabled={saving}
          >
            <Tags size={16} /> Categories
          </button>
          <button
            className="fa-btn fa-btn-primary"
            onClick={save}
            disabled={saving}
          >
            <Save size={16} /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      <div className="fa-split">
        <div className="fa-panel">
          <div className="fa-toolbar">
            <input
              className="fa-input"
              placeholder="Asset Tag (optional; auto-generated if blank)"
              value={form.asset_tag}
              onChange={(e) => setField("asset_tag", e.target.value)}
              disabled={saving}
            />
            <input
              className="fa-input"
              placeholder="Asset Name"
              value={form.name}
              onChange={(e) => setField("name", e.target.value)}
              disabled={saving}
            />

            <select
              className="fa-select"
              value={form.category_id}
              onChange={(e) => {
                const id = e.target.value;
                setField("category_id", id);
                applyCategoryDefaults(id);
              }}
              disabled={saving}
            >
              <option value="">Select Category (optional)</option>
              {cats.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>

            <input
              className="fa-input"
              placeholder="Category label (optional)"
              value={form.category}
              onChange={(e) => setField("category", e.target.value)}
              disabled={saving}
            />
          </div>

          <textarea
            className="fa-textarea"
            rows={3}
            placeholder="Description (optional)"
            value={form.description}
            onChange={(e) => setField("description", e.target.value)}
            disabled={saving}
          />

          <div className="fa-toolbar" style={{ marginTop: 10 }}>
            <div>
              <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 6 }}>
                Purchase Date
              </div>
              <input
                className="fa-input"
                type="date"
                value={form.purchase_date}
                onChange={(e) => setField("purchase_date", e.target.value)}
                disabled={saving}
              />
            </div>
            <div>
              <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 6 }}>
                In Service Date
              </div>
              <input
                className="fa-input"
                type="date"
                value={form.in_service_date}
                onChange={(e) => setField("in_service_date", e.target.value)}
                disabled={saving}
              />
            </div>
            <div>
              <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 6 }}>
                Status
              </div>
              <select
                className="fa-select"
                value={form.status}
                onChange={(e) => setField("status", e.target.value)}
                disabled={saving}
              >
                <option value="ACTIVE">ACTIVE</option>
                <option value="DRAFT">DRAFT</option>
                <option value="DISPOSED">DISPOSED</option>
                <option value="WRITTEN_OFF">WRITTEN_OFF</option>
              </select>
            </div>
          </div>

          <div className="fa-toolbar" style={{ marginTop: 10 }}>
            <input
              className="fa-input"
              placeholder="Location (optional)"
              value={form.location}
              onChange={(e) => setField("location", e.target.value)}
              disabled={saving}
            />
            <input
              className="fa-input"
              placeholder="Department (optional)"
              value={form.department}
              onChange={(e) => setField("department", e.target.value)}
              disabled={saving}
            />
            <input
              className="fa-input"
              placeholder="Custodian (optional)"
              value={form.custodian}
              onChange={(e) => setField("custodian", e.target.value)}
              disabled={saving}
            />
          </div>

          <div className="fa-toolbar" style={{ marginTop: 10 }}>
            <input
              className="fa-input"
              placeholder="Vendor (optional)"
              value={form.vendor_name}
              onChange={(e) => setField("vendor_name", e.target.value)}
              disabled={saving}
            />
            <input
              className="fa-input"
              placeholder="Invoice Ref (optional)"
              value={form.invoice_ref}
              onChange={(e) => setField("invoice_ref", e.target.value)}
              disabled={saving}
            />
          </div>

          {/* Phase 3.6: Acquisition Posting */}
          {!isEdit && (
            <div className="fa-card" style={{ marginTop: 12 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 10,
                  alignItems: "center",
                }}
              >
                <div>
                  <div
                    style={{
                      fontWeight: 900,
                      display: "flex",
                      gap: 8,
                      alignItems: "center",
                    }}
                  >
                    <Wallet size={16} /> Acquisition Journal
                  </div>
                  <div style={{ opacity: 0.8, fontSize: 13, marginTop: 4 }}>
                    Optionally post the purchase to the ledger immediately.
                  </div>
                </div>

                <label
                  style={{
                    display: "flex",
                    gap: 10,
                    alignItems: "center",
                    fontSize: 13,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={postAcqNow}
                    onChange={(e) => setPostAcqNow(e.target.checked)}
                    disabled={saving}
                  />
                  Post now
                </label>
              </div>

              {postAcqNow && (
                <div style={{ marginTop: 10 }}>
                  <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 6 }}>
                    Paid from / Payable account (Credit)
                  </div>
                  <select
                    className="fa-select"
                    value={creditAccountId}
                    onChange={(e) => setCreditAccountId(e.target.value)}
                    disabled={saving}
                    style={{ width: "100%" }}
                  >
                    <option value="">Select account…</option>
                    {acquisitionCreditAccounts.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.code ? `${a.code} - ` : ""}
                        {a.name} ({a.type}/{a.category || "—"})
                      </option>
                    ))}
                  </select>

                  <div style={{ marginTop: 6, fontSize: 12, opacity: 0.75 }}>
                    Ledger entry: <b>Dr Fixed Asset</b> •{" "}
                    <b>Cr selected account</b>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="fa-panel">
          <div style={{ fontWeight: 800, marginBottom: 10 }}>Depreciation</div>

          <div className="fa-toolbar">
            <input
              className="fa-input"
              type="number"
              placeholder="Cost"
              value={form.cost}
              onChange={(e) => setField("cost", e.target.value)}
              disabled={saving}
            />
            <input
              className="fa-input"
              type="number"
              placeholder="Residual Value"
              value={form.residual_value}
              onChange={(e) => setField("residual_value", e.target.value)}
              disabled={saving}
            />
          </div>

          <div className="fa-toolbar">
            <input
              className="fa-input"
              type="number"
              placeholder="Useful life (months)"
              value={form.useful_life_months}
              onChange={(e) => setField("useful_life_months", e.target.value)}
              disabled={saving}
            />
            <select
              className="fa-select"
              value={form.depreciation_method}
              onChange={(e) => setField("depreciation_method", e.target.value)}
              disabled={saving}
            >
              <option value="SL">Straight Line</option>
            </select>
          </div>

          <label
            style={{
              display: "flex",
              gap: 10,
              alignItems: "center",
              marginTop: 10,
              fontSize: 13,
              opacity: 0.9,
            }}
          >
            <input
              type="checkbox"
              checked={form.prorate_first_month}
              onChange={(e) =>
                setField("prorate_first_month", e.target.checked)
              }
              disabled={saving}
            />
            Prorate first month based on in-service date
          </label>

          <div className="fa-card" style={{ marginTop: 10 }}>
            <div className="label">Depreciable Base</div>
            <div className="value">{formatNaira(computed.base)}</div>
            <div className="hint">
              Monthly depreciation (SL): <b>{formatNaira(computed.monthly)}</b>
            </div>
            <div className="hint">
              Year 1 depreciation (est.): <b>{formatNaira(computed.year1)}</b>
            </div>
          </div>

          <div style={{ fontWeight: 800, margin: "14px 0 10px" }}>
            Accounting Mapping
          </div>

          <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 6 }}>
            Fixed Asset Account (cost)
          </div>
          <select
            className="fa-select"
            value={form.asset_account_id}
            onChange={(e) => setField("asset_account_id", e.target.value)}
            style={{ width: "100%" }}
            disabled={saving}
          >
            <option value="">Select account…</option>
            {assetAccounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.code ? `${a.code} - ` : ""}
                {a.name} ({a.category})
              </option>
            ))}
          </select>

          <div style={{ fontSize: 12, opacity: 0.8, margin: "10px 0 6px" }}>
            Accumulated Depreciation Account
          </div>
          <select
            className="fa-select"
            value={form.accum_dep_account_id}
            onChange={(e) => setField("accum_dep_account_id", e.target.value)}
            style={{ width: "100%" }}
            disabled={saving}
          >
            <option value="">Select account…</option>
            {assetAccounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.code ? `${a.code} - ` : ""}
                {a.name} ({a.category})
              </option>
            ))}
          </select>

          <div style={{ fontSize: 12, opacity: 0.8, margin: "10px 0 6px" }}>
            Depreciation Expense Account
          </div>
          <select
            className="fa-select"
            value={form.dep_expense_account_id}
            onChange={(e) => setField("dep_expense_account_id", e.target.value)}
            style={{ width: "100%" }}
            disabled={saving}
          >
            <option value="">Select account…</option>
            {expenseAccounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.code ? `${a.code} - ` : ""}
                {a.name} ({a.category})
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}
